import { avatarDefault } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Avatar, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import NotificationButton from '@/features/guardian/components/notification-button';
import { useGuardianProfileQuery } from '@/features/guardian/queries';
import { createStyles, useScaling } from '@/hooks';
import { useSessionStore } from '@/stores';
import { colors, guardianColors } from '@/theme/colors';
import { format } from 'date-fns';
import { useMemo, type ComponentProps } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type UserProfileHeaderProps = {
  onNotificationPress?: () => void;
  hasUnreadNotification?: boolean;
};

type HeaderIdentityProps = {
  avatarSource: ComponentProps<typeof Avatar.Image>['source'];
  greeting: string;
  greetingIcon: string;
  isTablet: boolean;
  name: string;
  todayLabel: string;
};

const AVATAR_SIZE = 40;

export default function UserProfileHeader({
  onNotificationPress,
  hasUnreadNotification = true,
}: UserProfileHeaderProps) {
  const userId = useSessionStore(state => state.user?.uid);
  const { data: guardianProfile } = useGuardianProfileQuery(userId);
  const { isTablet, scale } = useScaling();
  const insets = useSafeAreaInsets();

  const name = useMemo(() => {
    return guardianProfile?.firstname ?? '';
  }, [guardianProfile?.firstname]);

  const avatarSource = useMemo(() => {
    const photoUrl = guardianProfile?.photoUrl;
    return photoUrl ? { uri: photoUrl } : avatarDefault;
  }, [guardianProfile?.photoUrl]);

  const { greeting, greetingIcon } = useMemo(() => {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 12) {
      return { greeting: STRINGS.guardian.header.goodMorning, greetingIcon: '🌤️' };
    }
    if (hour >= 12 && hour < 18) {
      return { greeting: STRINGS.guardian.header.goodAfternoon, greetingIcon: '🌤️' };
    }
    if (hour >= 18 && hour < 22) {
      return { greeting: STRINGS.guardian.header.goodEvening, greetingIcon: '🌙' };
    }
    return { greeting: STRINGS.guardian.header.late, greetingIcon: '🌙' };
  }, []);
  const todayLabel = useMemo(() => format(new Date(), 'EEEE · MMM d'), []);

  return (
    <Stack
      direction="row"
      align="center"
      justify="space-between"
      backgroundColor={isTablet ? colors['0'] : guardianColors['1']}
      paddingTop={insets.top + scale(isTablet ? 12 : 8)}
      paddingBottom={isTablet ? scale(12) : undefined}
      paddingX={scale(isTablet ? 18 : 20)}>
      <HeaderIdentity
        avatarSource={avatarSource}
        greeting={greeting}
        greetingIcon={greetingIcon}
        isTablet={isTablet}
        name={name}
        todayLabel={todayLabel}
      />
      <NotificationButton
        hasUnreadNotification={hasUnreadNotification}
        onPress={onNotificationPress}
      />
    </Stack>
  );
}

const HeaderIdentity = ({
  avatarSource,
  greeting,
  greetingIcon,
  isTablet,
  name,
  todayLabel,
}: HeaderIdentityProps) => {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Stack direction="row" align="center" flex={1} gap={scale(8)}>
      <Avatar size={scale(AVATAR_SIZE)} borderWidth={1} borderColor={colors['3']} activeOpacity={1}>
        <Avatar.Image source={avatarSource} backgroundColor={colors['1']} />
      </Avatar>

      <Stack flex={1}>
        <Text
          numberOfLines={1}
          variant={
            isTablet
              ? 'Font Guardian App/Body/Body 16 Bold'
              : 'Font Guardian App/Subtitle/Subtitle 12 Regular'
          }
          style={isTablet ? styles.name : styles.greeting}>
          {isTablet ? `${greeting}, ${name} ${greetingIcon}` : `${greeting} ${greetingIcon}`}
        </Text>
        <Text
          numberOfLines={1}
          variant={
            isTablet
              ? 'Font Guardian App/Subtitle/Subtitle 11 Regular'
              : 'Font Guardian App/Body/Body 16 Bold'
          }
          style={isTablet ? styles.tabletDate : styles.name}>
          {isTablet ? todayLabel : name}
        </Text>
      </Stack>
    </Stack>
  );
};

const useStyles = createStyles(() => ({
  avatar: {
    shadowColor: '#0F225B',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
    elevation: 2,
  },
  greeting: {
    color: colors['10'],
  },
  name: {
    color: colors['10'],
  },
  tabletDate: {
    color: guardianColors['6'],
  },
}));
